import React, { useMemo, useState } from "react";
import { Plus, Minus, GripVertical, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../../../lib/utils";
import { catalogForChannels } from "./reportsCatalog";

const TYPE_BADGE = { kpi: "KPI", breakdown: "Bar", table: "Table", timeseries: "Trend" };

export default function MetricPalette({ connections, onAdd, onRemove, placedCatalogIds }) {
  const [query, setQuery] = useState("");
  const groups = useMemo(() => catalogForChannels(connections), [connections]);

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({ ...g, metrics: g.metrics.filter((m) => m.label.toLowerCase().includes(q)) }))
      .filter((g) => g.metrics.length);
  }, [groups, query]);

  if (!groups.length) {
    return (
      <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
        No connected channels yet. Connect a channel on the{" "}
        <Link to="/channels" className="font-medium text-violet-600 hover:underline">
          Channels
        </Link>{" "}
        page to see available metrics.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search metrics"
          className="w-full rounded-md border border-input bg-background py-1.5 pl-8 pr-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>

      <div className="flex flex-col gap-4">
        {filteredGroups.map((group) => (
          <div key={group.channel.key}>
            <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {group.channel.label}
            </h3>
            <ul className="flex flex-col gap-1">
              {group.metrics.map((metric) => {
                const placed = placedCatalogIds.includes(metric.id);
                return (
                  <li
                    key={metric.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("application/x-mb-metric", metric.id);
                      e.dataTransfer.effectAllowed = "copy";
                    }}
                    className="group flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5 text-sm hover:border-violet-500/40 hover:bg-accent"
                  >
                    <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-muted-foreground active:cursor-grabbing" />
                    <span className="min-w-0 flex-1 truncate text-foreground">{metric.label}</span>
                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                      {TYPE_BADGE[metric.type] || metric.type}
                    </span>
                    {placed ? (
                      <button
                        type="button"
                        onClick={() => onRemove(metric.id)}
                        title="Remove from report"
                        aria-label="Remove metric from report"
                        className="shrink-0 rounded p-1 text-red-600 hover:bg-red-600 hover:text-white"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onAdd(metric.id)}
                        title="Add to report"
                        aria-label="Add metric to report"
                        className="shrink-0 rounded p-1 text-violet-600 hover:bg-violet-600 hover:text-white"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {!filteredGroups.length ? (
          <p className="text-sm text-muted-foreground">No metrics match &ldquo;{query}&rdquo;.</p>
        ) : null}
      </div>
    </div>
  );
}
